import React, { useLayoutEffect } from "react";
import { useRecoilState } from "recoil";
import { Link } from "gatsby";
import { langState, cookieConsentState } from "../recoilAtom";
import {
  readConsentChoice,
  saveConsentChoice,
  loadAnalytics,
  disableAnalytics,
  trackPageView,
} from "./analytics";

const CookieBanner = () => {
  const cookieText: any = {
    "pl-PL": {
      h2: "Dbamy o Twoją prywatność",
      p: "Medinice S.A. używa plików cookie w celu ulepszenia i dostosowania doświadczeń użytkowników na naszej stronie internetowej. Wybierając „Zgoda”, wyrażasz zgodę na używanie wszystkich plików cookie, które zbierają i wykorzystują informacje o Twoich interakcjach z naszą stroną, aby dostosować treści do Twoich potrzeb i ułatwić korzystanie z serwisu.",
      link: "Polityka prywatności",
      button1: "Zgoda",
      button2: "Odmowa",
    },
    "en-US": {
      h2: "We Care About Your Privacy",
      p: "Medinice S.A. uses cookies to improve and customize users experience on our website. By selecting 'Accept', you consent to the use of all cookies that gather and use information about your interactions with our site to provide personalized content and enhance your digital experience.",
      link: "Privacy Policy",
      button1: "Accept",
      button2: "Decline",
    },
  };

  const [cookieAccepted, setCookieAccepted] =
    useRecoilState(cookieConsentState);
  const [lang, setLang] = useRecoilState(langState);

  useLayoutEffect(() => {
    setCookieAccepted(readConsentChoice() !== null);
  }, []);

  const answerCookie = (choice: "allow" | "deny") => {
    saveConsentChoice(choice);
    if (choice === "allow") {
      loadAnalytics();
      trackPageView(window.location);
    } else {
      disableAnalytics();
    }
    setCookieAccepted(true);
  };

  if (cookieAccepted) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed z-40 bottom-8 left-8 w-[520px] max-w-[calc(100%-64px)] max-lg:bottom-4 max-lg:left-4 max-lg:right-4 max-lg:w-auto max-lg:max-w-[calc(100%-32px)] fade-in-from-left"
    >
      <div className="overflow-hidden rounded-2xl bg-white border border-black/5 shadow-[0px_24px_60px_-15px_rgba(28,26,26,0.35)]">
        <div className="h-1 w-full bg-blue-600" />

        <div className="flex flex-col gap-3 px-6 pt-6 pb-5 max-lg:px-4 max-lg:pt-5 max-lg:pb-4">
          <div className="flex items-center gap-3">
            <span className="flex shrink-0 items-center justify-center h-9 w-9 rounded-full bg-blue-50 text-blue-600">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Z" />
                <path d="M8.5 9h.01M12 14h.01M16 11.5h.01M9 16h.01" />
              </svg>
            </span>
            <h2
              id="cookie-banner-title"
              className="text-xl font-bold leading-snug max-lg:text-lg"
            >
              {cookieText[lang].h2}
            </h2>
          </div>

          <p className="text-sm leading-relaxed text-gray-600">
            {cookieText[lang].p}
          </p>

          <Link
            to="/privacy-policy"
            className="w-max text-sm font-semibold text-blue-600 underline underline-offset-4 transition-colors duration-300 hover:text-blue-800"
          >
            {cookieText[lang].link}
          </Link>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-black/10 bg-blue-50 max-sm:flex-col max-sm:px-4">
          <button
            type="button"
            className="flex flex-1 items-center justify-center h-11 px-5 rounded-full border-2 border-solid border-blue-600 bg-white text-sm font-bold text-blue-600 cursor-pointer transition-colors duration-300 hover:bg-blue-100 max-lg:h-14 max-lg:text-base"
            onClick={() => answerCookie("deny")}
          >
            {cookieText[lang].button2}
          </button>
          <button
            type="button"
            className="flex flex-1 items-center justify-center h-11 px-5 rounded-full border-2 border-solid border-blue-600 bg-blue-600 text-sm font-bold text-white cursor-pointer transition-colors duration-300 hover:bg-blue-800 hover:border-blue-800 max-lg:h-14 max-lg:text-base"
            onClick={() => answerCookie("allow")}
          >
            {cookieText[lang].button1}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
